import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { FileText, Plus, Trash2, Upload, X } from 'lucide-react';
import axios from 'axios';

const InvoiceGenerator = () => {
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    invoice_number: '',
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    from_name: '',
    from_address: '',
    from_email: '',
    from_phone: '',
    to_name: '',
    to_address: '',
    to_email: '',
    tax_rate: 0,
    notes: '',
    currency: 'USD',
    payment_status: 'unpaid',
    amount_paid: 0,
  });

  const [items, setItems] = useState([
    { description: '', quantity: '', rate: '' }
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo must be less than 2MB');
        return;
      }
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        setError('Logo must be JPG or PNG');
        return;
      }
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: '', rate: '' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      return sum + (qty * rate);
    }, 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (parseFloat(formData.tax_rate) / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const calculateDueAmount = () => {
    if (formData.payment_status === 'partial') {
      return Math.max(0, calculateTotal() - parseFloat(formData.amount_paid || 0));
    }
    return 0;
  };

  const currencies = [
    { code: 'USD', name: 'United States Dollar', symbol: '$', country: 'United States' },
    { code: 'EUR', name: 'Euro', symbol: '€', country: 'European Union' },
    { code: 'GBP', name: 'British Pound', symbol: '£', country: 'United Kingdom' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', country: 'India' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', country: 'Japan' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', country: 'Australia' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', country: 'Canada' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', country: 'Switzerland' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', country: 'China' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', country: 'Sweden' },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', country: 'New Zealand' },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$', country: 'Mexico' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', country: 'Singapore' },
    { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', country: 'Hong Kong' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', country: 'Norway' },
    { code: 'KRW', name: 'South Korean Won', symbol: '₩', country: 'South Korea' },
    { code: 'TRY', name: 'Turkish Lira', symbol: '₺', country: 'Turkey' },
    { code: 'RUB', name: 'Russian Ruble', symbol: '₽', country: 'Russia' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', country: 'Brazil' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', country: 'South Africa' },
    { code: 'DKK', name: 'Danish Krone', symbol: 'kr', country: 'Denmark' },
    { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', country: 'Poland' },
    { code: 'THB', name: 'Thai Baht', symbol: '฿', country: 'Thailand' },
    { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', country: 'Indonesia' },
    { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', country: 'Hungary' },
    { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', country: 'Czech Republic' },
    { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', country: 'Israel' },
    { code: 'CLP', name: 'Chilean Peso', symbol: '$', country: 'Chile' },
    { code: 'PHP', name: 'Philippine Peso', symbol: '₱', country: 'Philippines' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', country: 'United Arab Emirates' },
    { code: 'COP', name: 'Colombian Peso', symbol: '$', country: 'Colombia' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', country: 'Saudi Arabia' },
    { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', country: 'Malaysia' },
    { code: 'RON', name: 'Romanian Leu', symbol: 'lei', country: 'Romania' },
    { code: 'ARS', name: 'Argentine Peso', symbol: '$', country: 'Argentina' },
    { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', country: 'Vietnam' },
    { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', country: 'Pakistan' },
    { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', country: 'Bangladesh' },
    { code: 'EGP', name: 'Egyptian Pound', symbol: '£', country: 'Egypt' },
    { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', country: 'Nigeria' },
    { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', country: 'Ukraine' },
    { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', country: 'Peru' },
    { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', country: 'Kenya' },
    { code: 'LKR', name: 'Sri Lankan Rupee', symbol: '₨', country: 'Sri Lanka' },
    { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.', country: 'Morocco' },
    { code: 'QAR', name: 'Qatari Riyal', symbol: '﷼', country: 'Qatar' },
    { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', country: 'Kuwait' },
    { code: 'OMR', name: 'Omani Rial', symbol: '﷼', country: 'Oman' },
    { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', country: 'Bahrain' },
    { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا', country: 'Jordan' },
  ];



  const getCurrencySymbol = (currency) => {
    const curr = currencies.find(c => c.code === currency);
    return curr ? curr.symbol : currency;
  };

  const generateInvoice = async () => {
    console.log('Generate invoice clicked');
    
    if (!formData.invoice_number || !formData.from_name || !formData.to_name) {
      setError('Please fill in required fields: Invoice Number, From Name, and To Name');
      return;
    }

    if (items.some(item => !item.description || !item.quantity || !item.rate || parseFloat(item.quantity) <= 0 || parseFloat(item.rate) <= 0)) {
      setError('All items must have description, quantity > 0, and rate > 0');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      const invoiceData = {
        ...formData,
        items: items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          rate: parseFloat(item.rate)
        }))
      };
      
      console.log('Sending invoice data:', invoiceData);
      formDataToSend.append('invoice_data', JSON.stringify(invoiceData));
      
      if (logo) {
        formDataToSend.append('logo', logo);
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      console.log('API URL:', apiUrl);

      const response = await axios.post(
        `${apiUrl}/api/invoice/generate`,
        formDataToSend,
        { responseType: 'blob' }
      );

      console.log('Response received:', response.status);

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${formData.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('Invoice downloaded successfully');
    } catch (err) {
      console.error('Invoice generation error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Invoice Document */}
        <div className="bg-white shadow-2xl rounded-lg p-12 mb-6">
          {/* Header with Logo */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-slate-200">
            <div className="flex-1">
              <h1 className="text-5xl font-bold text-slate-900 mb-2">{formData.company_name || 'INVOICE'}</h1>
              <div className="mt-4">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Company Name (Optional)</Label>
                <Input 
                  name="company_name" 
                  value={formData.company_name} 
                  onChange={handleInputChange} 
                  placeholder="Leave blank to show 'INVOICE'" 
                  className="mt-1 border-0 border-b-2 border-slate-200 rounded-none px-0 focus:border-slate-900 max-w-md" 
                />
              </div>
              <div className="flex items-center gap-4 mt-4">
                <Label className="text-sm font-medium text-slate-600">Currency:</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                  <SelectTrigger className="w-48 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="max-h-80 overflow-y-auto">
                      {currencies.map(curr => (
                        <SelectItem key={curr.code} value={curr.code}>
                          {curr.country} - {curr.code} ({curr.symbol})
                        </SelectItem>
                      ))}
                    </div>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-right">
              {logoPreview ? (
                <div className="relative inline-block mb-2">
                  <img src={logoPreview} alt="Logo" className="h-20 w-auto border rounded" />
                  <button onClick={removeLogo} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <label className="inline-block cursor-pointer border-2 border-dashed border-slate-300 rounded p-4 hover:border-slate-400 transition-colors mb-2">
                  <Upload className="h-8 w-8 mx-auto text-slate-400 mb-1" />
                  <span className="text-xs text-slate-500 block">Add Logo</span>
                  <input type="file" accept="image/jpeg,image/png" onChange={handleLogoUpload} className="hidden" />
                </label>
              )}
            </div>
          </div>

          {/* Invoice Details */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Invoice Number <span className="text-red-500">*</span></Label>
                <Input name="invoice_number" value={formData.invoice_number} onChange={handleInputChange} placeholder="INV-001" className="mt-1 border-0 border-b-2 border-slate-200 rounded-none px-0 focus:border-slate-900" required />
              </div>
              <div className="mb-4">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</Label>
                <Input name="date" type="date" value={formData.date} onChange={handleInputChange} className="mt-1 border-0 border-b-2 border-slate-200 rounded-none px-0 focus:border-slate-900" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Due Date</Label>
                <Input name="due_date" type="date" value={formData.due_date} onChange={handleInputChange} className="mt-1 border-0 border-b-2 border-slate-200 rounded-none px-0 focus:border-slate-900" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Payment Status</Label>
              <Select value={formData.payment_status} onValueChange={(value) => setFormData(prev => ({ ...prev, payment_status: value }))}>
                <SelectTrigger className="border-0 border-b-2 border-slate-200 rounded-none px-0 focus:border-slate-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="paid">Fully Paid</SelectItem>
                  <SelectItem value="partial">Partially Paid</SelectItem>
                </SelectContent>
              </Select>
              {formData.payment_status === 'partial' && (
                <div className="mt-4">
                  <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount Paid</Label>
                  <Input name="amount_paid" type="number" value={formData.amount_paid} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" className="mt-1 border-0 border-b-2 border-slate-200 rounded-none px-0 focus:border-slate-900" />
                </div>
              )}
            </div>
          </div>

          {/* From/To Section */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div className="bg-slate-50 p-6 rounded-lg">
              <h3 className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wide">From <span className="text-red-500">*</span></h3>
              <Input name="from_name" value={formData.from_name} onChange={handleInputChange} placeholder="Your Name/Company *" className="mb-3 bg-white" required />
              <Input name="from_address" value={formData.from_address} onChange={handleInputChange} placeholder="Address" className="mb-3 bg-white" />
              <Input name="from_email" type="email" value={formData.from_email} onChange={handleInputChange} placeholder="Email" className="mb-3 bg-white" />
              <Input name="from_phone" value={formData.from_phone} onChange={handleInputChange} placeholder="Phone" className="bg-white" />
            </div>
            <div className="bg-slate-50 p-6 rounded-lg">
              <h3 className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wide">Bill To <span className="text-red-500">*</span></h3>
              <Input name="to_name" value={formData.to_name} onChange={handleInputChange} placeholder="Client Name/Company *" className="mb-3 bg-white" required />
              <Input name="to_address" value={formData.to_address} onChange={handleInputChange} placeholder="Address" className="mb-3 bg-white" />
              <Input name="to_email" type="email" value={formData.to_email} onChange={handleInputChange} placeholder="Email" className="bg-white" />
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <div className="bg-slate-900 text-white grid grid-cols-12 gap-2 p-3 rounded-t-lg font-semibold text-sm">
              <div className="col-span-6">DESCRIPTION</div>
              <div className="col-span-2 text-center">QTY</div>
              <div className="col-span-2 text-center">RATE</div>
              <div className="col-span-2 text-right">AMOUNT</div>
            </div>
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 p-3 border-b border-slate-200 items-center hover:bg-slate-50">
                <div className="col-span-6">
                  <Input value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} placeholder="Item description" className="border-0 border-b border-slate-200 rounded-none px-0 h-8" />
                </div>
                <div className="col-span-2">
                  <Input type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} placeholder="1" min="1" className="border-0 border-b border-slate-200 rounded-none px-0 h-8 text-center" />
                </div>
                <div className="col-span-2">
                  <Input type="number" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} placeholder="0.00" min="0" step="0.01" className="border-0 border-b border-slate-200 rounded-none px-0 h-8 text-center" />
                </div>
                <div className="col-span-1 text-right font-medium">
                  {getCurrencySymbol(formData.currency)}{((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)).toFixed(2)}
                </div>
                <div className="col-span-1 text-right">
                  <button onClick={() => removeItem(index)} disabled={items.length === 1} className="text-red-500 hover:text-red-700 disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            <button onClick={addItem} className="w-full p-3 border-2 border-dashed border-slate-300 rounded-b-lg text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" />
              Add Item
            </button>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-80">
              <div className="flex justify-between py-2 border-b border-slate-200">
                <span className="text-slate-600">Subtotal:</span>
                <span className="font-medium">{getCurrencySymbol(formData.currency)}{calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-200 items-center">
                <span className="text-slate-600">Tax:</span>
                <div className="flex items-center gap-2">
                  <Input name="tax_rate" type="number" value={formData.tax_rate} onChange={handleInputChange} min="0" step="0.01" placeholder="0" className="w-16 h-8 text-right border-0 border-b border-slate-200 rounded-none px-1" />
                  <span className="text-slate-600">%</span>
                  <span className="font-medium w-20 text-right">{getCurrencySymbol(formData.currency)}{calculateTax().toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between py-3 border-t-2 border-slate-900 mt-2">
                <span className="text-lg font-bold">TOTAL:</span>
                <span className="text-lg font-bold">{getCurrencySymbol(formData.currency)}{calculateTotal().toFixed(2)}</span>
              </div>
              {formData.payment_status === 'partial' && (
                <>
                  <div className="flex justify-between py-2 text-green-600">
                    <span>Amount Paid:</span>
                    <span className="font-medium">{getCurrencySymbol(formData.currency)}{parseFloat(formData.amount_paid || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 text-red-600 font-bold">
                    <span>Balance Due:</span>
                    <span>{getCurrencySymbol(formData.currency)}{calculateDueAmount().toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="border-t-2 border-slate-200 pt-6">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Notes</Label>
            <Textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} placeholder="Payment terms, thank you message, or additional information..." className="resize-none" />
          </div>
        </div>

        {/* Generate Button */}
        <div className="text-center">
          <Button onClick={generateInvoice} disabled={loading} size="lg" className="px-12 py-6 text-lg bg-slate-900 hover:bg-slate-800">
            <FileText className="mr-2 h-6 w-6" />
            {loading ? 'Generating PDF...' : 'Generate Invoice PDF'}
          </Button>
          <p className="text-sm text-slate-500 mt-4">Your data is processed in-memory only and never saved</p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceGenerator;
